import { Link, useParams } from 'react-router-dom';
import { articles, getArticleBySlug } from '@/mocks/articles';
import SEO from '@/components/feature/SEO';
import type { Article } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="pt-16 min-h-screen bg-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl" />
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-600 text-sm mb-6">该文章可能已被移除或链接无效</p>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回新闻列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 2);

  return (
    <>
      <SEO
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalPath={`/articles/${article.slug}`}
        ogType="article"
      />

      <main className="pt-16 bg-background-50">
        {/* Header */}
        <section className="bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-primary-600 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.author}</span>
            </div>
            <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.summary}
            </p>
          </div>
        </section>

        {/* Cover Image */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6 -mt-4">
          <div className="h-56 md:h-80 lg:h-96 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Content */}
        <article className="max-w-[900px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 prose-headings:text-foreground-900 prose-headings:font-heading prose-a:text-primary-600 prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
            <span className="text-xs text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-primary-50 text-primary-700 px-3 py-1 rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/articles/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {ra.category}
                        </span>
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back Link */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-600 text-sm font-medium transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回新闻列表
          </Link>
        </div>
      </main>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          datePublished: article.date,
          author: {
            '@type': 'Organization',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '尊龙集团',
          },
          url: `https://www.zunlonggroup.com/articles/${article.slug}`,
        })}
      </script>
    </>
  );
}